/**
 * Advanced Analytics Service
 * Comprehensive user behavior tracking and business intelligence
 */

class AdvancedAnalytics {
  constructor() {
    this.sessionId = this.generateSessionId();
    this.userId = null;
    this.userRole = null;
    this.agencyId = null;
    this.startTime = Date.now();
    this.events = [];
    this.pageViews = [];
    this.userJourney = [];
    this.performanceMetrics = {};
    this.isEnabled = this.checkIfEnabled();
    this.batchSize = 10;
    this.batchTimeout = 5000;
    this.batchTimer = null;
    
    this.initializeTracking();
  }

  /**
   * Check if analytics is enabled
   */
  checkIfEnabled() {
    return typeof window !== 'undefined' && (
      process.env.NODE_ENV === 'production' ||
      localStorage.getItem('parkpro-analytics') === 'true' ||
      new URLSearchParams(window.location.search).get('analytics') === 'true'
    );
  }

  /**
   * Generate unique session ID
   */
  generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Set user context
   */
  setUserContext(user) {
    this.userId = user?.id || user?.userid;
    this.userRole = user?.role;
    this.agencyId = user?.agencyId || user?.useragentid;
    
    this.trackEvent('user_context_set', {
      userId: this.userId,
      userRole: this.userRole,
      agencyId: this.agencyId,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Track page view
   */
  trackPageView(page = null) {
    const pageData = {
      page: page || window.location.pathname,
      title: document.title,
      referrer: document.referrer,
      timestamp: new Date().toISOString(),
      sessionId: this.sessionId,
      userId: this.userId,
      userRole: this.userRole,
      agencyId: this.agencyId,
      url: window.location.href,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      }
    };

    this.pageViews.push(pageData);
    this.userJourney.push({
      type: 'page_view',
      ...pageData
    });

    this.trackEvent('page_view', pageData);
  }

  /**
   * Track custom events
   */
  trackEvent(eventName, properties = {}) {
    if (!this.isEnabled) return;

    const event = {
      eventName,
      properties: {
        ...properties,
        timestamp: new Date().toISOString(),
        sessionId: this.sessionId,
        userId: this.userId,
        userRole: this.userRole,
        agencyId: this.agencyId,
        page: window.location.pathname,
        url: window.location.href
      }
    };

    this.events.push(event);
    this.userJourney.push({
      type: 'custom_event',
      ...event
    });

    // Batch send events
    this.batchSendEvents();
  }

  /**
   * Track user interactions
   */
  trackUserInteractions() {
    // Track clicks
    document.addEventListener('click', (e) => {
      const element = e.target;
      const tagName = element.tagName.toLowerCase();
      
      // Track button clicks
      if (tagName === 'button' || element.classList.contains('btn')) {
        this.trackEvent('button_click', {
          buttonText: element.textContent?.trim(),
          buttonId: element.id,
          buttonClass: element.className,
          href: element.href,
          target: element.target
        });
      }
      
      // Track link clicks
      if (tagName === 'a') {
        this.trackEvent('link_click', {
          linkText: element.textContent?.trim(),
          linkHref: element.href,
          linkTarget: element.target,
          isExternal: !element.href.includes(window.location.origin)
        });
      }
    });

    // Track form submissions
    document.addEventListener('submit', (e) => {
      const form = e.target;
      this.trackEvent('form_submit', {
        formId: form.id,
        formClass: form.className,
        formAction: form.action,
        formMethod: form.method,
        fieldCount: form.elements.length
      });
    });
  }

  /**
   * Track business events
   */
  trackBusinessEvents() {
    // Track trip creation
    this.trackEvent('trip_creation_started', {
      source: 'questionnaire',
      timestamp: new Date().toISOString()
    });

    // Track itinerary generation
    this.trackEvent('itinerary_generation_started', {
      tripId: null, // Will be set when available
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Track conversion events
   */
  trackConversion(eventName, value = null, currency = 'USD') {
    this.trackEvent('conversion', {
      conversionType: eventName,
      value,
      currency,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Track user journey milestones
   */
  trackMilestone(milestone, data = {}) {
    this.trackEvent('milestone_reached', {
      milestone,
      ...data,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Track errors
   */
  trackError(error, context = {}) {
    this.trackEvent('error_occurred', {
      errorMessage: error.message,
      errorStack: error.stack,
      errorType: error.name,
      context,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Initialize tracking system
   */
  initializeTracking() {
    if (!this.isEnabled) return;

    // Track page load
    this.trackPageView();
    
    // Set up event listeners
    this.setupEventListeners();
    
    // Track user interactions
    this.trackUserInteractions();
    
    // Track business events
    this.trackBusinessEvents();
  }

  /**
   * Set up event listeners
   */
  setupEventListeners() {
    // Track performance metrics
    if (typeof window !== 'undefined' && window.performance) {
      window.addEventListener('load', () => {
        const perfData = window.performance.timing;
        
        this.performanceMetrics = {
          pageLoadTime: perfData.loadEventEnd - perfData.navigationStart,
          domContentLoaded: perfData.domContentLoadedEventEnd - perfData.navigationStart,
          timestamp: new Date().toISOString()
        };

        this.trackEvent('performance_metrics', this.performanceMetrics);
      });
    }
  }

  /**
   * Batch send events to backend
   */
  batchSendEvents() {
    if (this.events.length >= this.batchSize) {
      this.sendEvents();
    } else if (!this.batchTimer) {
      this.batchTimer = setTimeout(() => {
        this.sendEvents();
      }, this.batchTimeout);
    }
  }

  /**
   * Send events to backend
   */
  async sendEvents() {
    if (this.events.length === 0) return;

    const eventsToSend = [...this.events];
    this.events = [];

    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
      this.batchTimer = null;
    }

    const apiUrl = import.meta.env.VITE_API_URL;
    if (!apiUrl) {
      console.warn('VITE_API_URL is not set, cannot send analytics events');
      // Re-add events to queue for retry
      this.events.unshift(...eventsToSend);
      return;
    }

    try {
      const response = await fetch(`${apiUrl}/api/analytics/events`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          events: eventsToSend,
          sessionId: this.sessionId,
          userId: this.userId,
          userRole: this.userRole,
          agencyId: this.agencyId
        })
      });

      if (!response.ok) {
        console.warn('Failed to send analytics events:', response.status);
        // Re-add events to queue for retry
        this.events.unshift(...eventsToSend);
      }
    } catch (error) {
      console.warn('Error sending analytics events:', error);
      // Re-add events to queue for retry
      this.events.unshift(...eventsToSend);
    }
  }

  /**
   * Get analytics data
   */
  getAnalyticsData() {
    return {
      sessionId: this.sessionId,
      userId: this.userId,
      userRole: this.userRole,
      agencyId: this.agencyId,
      startTime: this.startTime,
      events: this.events,
      pageViews: this.pageViews,
      userJourney: this.userJourney,
      performanceMetrics: this.performanceMetrics
    };
  }

  /**
   * Enable/disable analytics
   */
  enable() {
    this.isEnabled = true;
    if (typeof window !== 'undefined') {
      localStorage.setItem('parkpro-analytics', 'true');
    }
  }

  disable() {
    this.isEnabled = false;
    if (typeof window !== 'undefined') {
      localStorage.removeItem('parkpro-analytics');
    }
  }
}

// Create singleton instance
const advancedAnalytics = new AdvancedAnalytics();

// Add global access for debugging
if (typeof window !== 'undefined') {
  window.parkproAnalytics = {
    track: (event, properties) => advancedAnalytics.trackEvent(event, properties),
    trackConversion: (event, value, currency) => advancedAnalytics.trackConversion(event, value, currency),
    trackMilestone: (milestone, data) => advancedAnalytics.trackMilestone(milestone, data),
    trackError: (error, context) => advancedAnalytics.trackError(error, context),
    setUserContext: (user) => advancedAnalytics.setUserContext(user),
    getData: () => advancedAnalytics.getAnalyticsData(),
    enable: () => advancedAnalytics.enable(),
    disable: () => advancedAnalytics.disable()
  };
}

export default advancedAnalytics;
